import { AdsClient } from "./ads.js";
import { CatalogClient } from "./catalog.js";
import { InspectionDraftsClient } from "./client/inspectionDrafts.js";
import { ExtensionsClient } from "./client/extensions.js";
import { ShippingClient } from "./shipping.js";
import { ApiError } from "./errors.js";
/**
 * LumoraClient is the single, typed connection point to the Lumora backend.
 * Every site and the admin portal talk to the backend exclusively through it,
 * so a backend change propagates by bumping this one package.
 */
export class LumoraClient extends InspectionDraftsClient {
    /** Priced catalog lines: menu items and shop services as one concept (#1044). */
    catalog = new CatalogClient(this, (method, path, body, authorized) => this.request(method, path, body, authorized));
    /** Promote any catalog object as an ad (epic #1117). */
    ads = new AdsClient((method, path, body, authorized) => this.request(method, path, body, authorized));
    /** Customer shipping quotes and the owner label desk (#1114). */
    shipping = new ShippingClient((method, path, body, authorized) => this.request(method, path, body, authorized));
    // ---- Timekeeping + embedded payroll (#1170, Refs #1138) ----
    async listTimecards(slug, params = {}) {
        const res = await this.request("GET", `${this.siteBase(slug)}/timecards${LumoraClient.query({ ...params })}`, undefined, true);
        return res.timecards;
    }
    /** Upsert (send `id` to update). */
    async saveTimecard(slug, input) {
        return this.request("POST", `${this.siteBase(slug)}/timecards`, input, true);
    }
    async submitTimecard(slug, id) {
        return this.request("POST", `${this.siteBase(slug)}/timecards/${encodeURIComponent(id)}/submit`, {}, true);
    }
    async approveTimecard(slug, id) {
        return this.request("POST", `${this.siteBase(slug)}/timecards/${encodeURIComponent(id)}/approve`, {}, true);
    }
    /** Rejects with a 404 ApiError when no company exists yet. */
    async getPayrollCompany(slug) {
        return this.request("GET", `${this.siteBase(slug)}/payroll/company`, undefined, true);
    }
    async createPayrollCompany(slug, input) {
        return this.request("POST", `${this.siteBase(slug)}/payroll/company`, input, true);
    }
    /** Move the company from onboarding to active once the provider reports it ready (409 otherwise). */
    async activatePayrollCompany(slug) {
        return this.request("POST", `${this.siteBase(slug)}/payroll/company/activate`, {}, true);
    }
    async listPayrollWorkplaces(slug) {
        const res = await this.request("GET", `${this.siteBase(slug)}/payroll/workplaces`, undefined, true);
        return res.workplaces;
    }
    async createPayrollWorkplace(slug, input) {
        return this.request("POST", `${this.siteBase(slug)}/payroll/workplaces`, input, true);
    }
    async listPayrollEmployees(slug) {
        const res = await this.request("GET", `${this.siteBase(slug)}/payroll/employees`, undefined, true);
        return res.employees;
    }
    async createPayrollEmployee(slug, input) {
        return this.request("POST", `${this.siteBase(slug)}/payroll/employees`, input, true);
    }
    async listPayRuns(slug) {
        const res = await this.request("GET", `${this.siteBase(slug)}/payroll/pay-runs`, undefined, true);
        return res.payRuns;
    }
    async createPayRun(slug, input) {
        return this.request("POST", `${this.siteBase(slug)}/payroll/pay-runs`, input, true);
    }
    async previewPayRun(slug, id) {
        return this.request("POST", `${this.siteBase(slug)}/payroll/pay-runs/${encodeURIComponent(id)}/preview`, {}, true);
    }
    async approvePayRun(slug, id) {
        return this.request("POST", `${this.siteBase(slug)}/payroll/pay-runs/${encodeURIComponent(id)}/approve`, {}, true);
    }
    /** Resolve a pay run stuck in "approving" by asking the payroll provider. */
    async reconcilePayRun(slug, id) {
        return this.request("POST", `${this.siteBase(slug)}/payroll/pay-runs/${encodeURIComponent(id)}/reconcile`, {}, true);
    }
    /** (Re)queue the run's manual-payment checks; idempotent, the retry path after a failed approve step. */
    async queuePaperChecks(slug, payRunId) {
        const res = await this.request("POST", `${this.siteBase(slug)}/payroll/pay-runs/${encodeURIComponent(payRunId)}/paper-checks`, {}, true);
        return res.paperChecks;
    }
    async listPaperChecks(slug, payRunId) {
        const res = await this.request("GET", `${this.siteBase(slug)}/payroll/paper-checks${LumoraClient.query({ payRun: payRunId })}`, undefined, true);
        return res.paperChecks;
    }
    async markPaperCheckPrinted(slug, id, checkNumber) {
        return this.request("POST", `${this.siteBase(slug)}/payroll/paper-checks/${encodeURIComponent(id)}/printed`, { checkNumber }, true);
    }
    /** Absolute URL of the check PDF (bearer header required; use `fetchPaperCheckPdf` for an authed Blob). */
    paperCheckPdfUrl(slug, id) {
        return `${this.baseUrl}${this.siteBase(slug)}/payroll/paper-checks/${encodeURIComponent(id)}/pdf`;
    }
    async fetchPaperCheckPdf(slug, id) {
        const headers = {};
        const token = this.getAccessToken();
        if (token)
            headers["Authorization"] = `Bearer ${token}`;
        const res = await this.fetchImpl(this.paperCheckPdfUrl(slug, id), { method: "GET", headers });
        if (!res.ok) {
            let err = {};
            try {
                err = JSON.parse(await res.text());
            }
            catch {
                /* non-JSON error body */
            }
            throw new ApiError(res.status, err.code ?? "error", err.error ?? res.statusText);
        }
        return res.blob();
    }
}
//# sourceMappingURL=client.js.map